import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchKonsinyeCariOzet,
  updateCariLimit,
} from '../services/techServicesService';
import './KonsinyeCariLimitCard.css';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
};

const progressClass = (pct) => {
  if (pct == null) return '';
  if (pct >= 100) return 'is-danger';
  if (pct >= 80) return 'is-warning';
  return '';
};

const KonsinyeCariLimitCard = ({
  techServiceId,
  canEditLimit = false,
  compact = false,
  ozet: ozetProp,
  loading: loadingProp,
  onUpdated,
}) => {
  const [ozet, setOzet] = useState(ozetProp ?? null);
  const [loading, setLoading] = useState(loadingProp ?? false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [limitInput, setLimitInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const loadOzet = useCallback(async () => {
    if (!techServiceId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchKonsinyeCariOzet(techServiceId);
      if (res?.basarili) {
        setOzet(res.veri);
      } else {
        setError(res?.mesaj || 'Konsinye cari bilgisi yüklenemedi');
      }
    } catch (err) {
      setError(err?.message || 'Konsinye cari bilgisi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [techServiceId]);

  useEffect(() => {
    if (ozetProp !== undefined || loadingProp !== undefined) {
      setOzet(ozetProp ?? null);
      setLoading(!!loadingProp);
      return undefined;
    }
    loadOzet();
    return undefined;
  }, [ozetProp, loadingProp, loadOzet]);

  const openModal = () => {
    setLimitInput(
      ozet?.cariLimit != null && ozet.cariLimit > 0 ? String(ozet.cariLimit) : ''
    );
    setModalError('');
    setModalOpen(true);
  };

  const handleSaveLimit = async () => {
    setModalError('');
    const trimmed = String(limitInput).trim();
    let cariLimit = null;
    if (trimmed !== '') {
      const num = Number(trimmed.replace(',', '.'));
      if (!Number.isFinite(num) || num < 0) {
        setModalError('Geçerli bir tutar girin (0 veya daha büyük).');
        return;
      }
      cariLimit = num;
    }
    setSaving(true);
    try {
      const res = await updateCariLimit(techServiceId, cariLimit);
      if (res?.basarili) {
        setModalOpen(false);
        await loadOzet();
        onUpdated?.();
        window.showNotification?.('success', 'Cari limit güncellendi');
      } else {
        setModalError(res?.mesaj || 'Cari limit güncellenemedi');
      }
    } catch (err) {
      setModalError(err?.message || 'Cari limit güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  if (compact) {
    if (loading) {
      return (
        <div className="konsinye-cari-mini">
          <div className="konsinye-cari-mini-skeleton" />
        </div>
      );
    }
    if (!ozet) return <span>—</span>;
    const pct = ozet.kullanimYuzdesi;
    const limitLabel = ozet.limitTanimli
      ? `${formatCurrency(ozet.toplamYuklenme ?? ozet.kullanimTutar)} / ${formatCurrency(ozet.cariLimit)}`
      : formatCurrency(ozet.kullanimTutar);
    return (
      <div className="konsinye-cari-mini">
        {ozet.limitTanimli && (
          <div className="konsinye-cari-mini-bar">
            <div
              className="konsinye-cari-mini-fill"
              style={{ width: `${Math.min(100, pct ?? 0)}%` }}
            />
          </div>
        )}
        <div className="konsinye-cari-mini-text">{limitLabel}</div>
      </div>
    );
  }

  return (
    <>
      <div className="konsinye-cari-card">
        <div className="konsinye-cari-card-header">
          <div className="konsinye-cari-card-title">Konsinye Cari Limit</div>
          {canEditLimit && techServiceId && (
            <button type="button" className="secondary-btn small" onClick={openModal}>
              Cari Limit Düzenle
            </button>
          )}
        </div>

        {loading && (
          <div className="konsinye-cari-skeleton">
            <div className="konsinye-cari-skeleton-line wide" />
            <div className="konsinye-cari-skeleton-line medium" />
            <div className="konsinye-cari-skeleton-bar" />
          </div>
        )}

        {!loading && error && <div className="error-state small">{error}</div>}

        {!loading && !error && ozet && (
          <>
            <div className="konsinye-cari-stats">
              <div>
                <span className="konsinye-cari-stat-label">Cari Limit</span>
                <span className="konsinye-cari-stat-value">
                  {ozet.limitTanimli ? formatCurrency(ozet.cariLimit) : 'Tanımsız'}
                </span>
              </div>
              <div>
                <span className="konsinye-cari-stat-label">Konsinye Kullanım</span>
                <span className="konsinye-cari-stat-value">
                  {formatCurrency(ozet.kullanimTutar)}
                </span>
              </div>
              {ozet.limitTanimli && (
                <div>
                  <span className="konsinye-cari-stat-label">Müsait</span>
                  <span className="konsinye-cari-stat-value">
                    {formatCurrency(ozet.musaitTutar)}
                  </span>
                </div>
              )}
            </div>

            {ozet.limitTanimli && (
              <div className="konsinye-cari-progress-wrap">
                <div className="konsinye-cari-progress-bar">
                  <div
                    className={`konsinye-cari-progress-fill ${progressClass(ozet.kullanimYuzdesi)}`}
                    style={{ width: `${Math.min(100, ozet.kullanimYuzdesi ?? 0)}%` }}
                  />
                </div>
                <div className="konsinye-cari-progress-label">
                  %{ozet.kullanimYuzdesi ?? 0} kullanımda
                  {ozet.bekleyenSiparisTutar > 0
                    ? ` (bekleyen sipariş: ${formatCurrency(ozet.bekleyenSiparisTutar)})`
                    : ''}
                </div>
              </div>
            )}

            <div className="konsinye-cari-note">
              Konsinye değeri güncel garanti fiyatlarıyla hesaplanır; fiyat değişince kullanım oranı değişebilir.
              {ozet.sonGuncelleme ? ` Son güncelleme: ${formatDateTime(ozet.sonGuncelleme)}.` : ''}
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <div className="konsinye-cari-modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="konsinye-cari-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Cari Limit Düzenle</h3>
            <label className="form-field">
              <span>Konsinye cari limit (TRY)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={limitInput}
                onChange={(e) => setLimitInput(e.target.value)}
                placeholder="Boş bırak = limit tanımsız"
              />
            </label>
            {modalError && <div className="error-state small">{modalError}</div>}
            <div className="konsinye-cari-modal-actions">
              <button type="button" className="secondary-btn" disabled={saving} onClick={() => setModalOpen(false)}>
                İptal
              </button>
              <button type="button" className="primary-btn" disabled={saving} onClick={handleSaveLimit}>
                {saving ? 'Kaydediliyor…' : 'Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KonsinyeCariLimitCard;
